import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {planOf} from "home/gating";
import {arrivedFromLadderLink} from "offers/ladder/ladderVisit";
import {storedReferralCode} from "referral/referralAttribution";
import {hasPendingReferral} from "referral/referralOffer";
import {useUser} from "UserProvider";
import type {StorefrontQuote} from "pricing/quotedPricing";
import {offersYieldToWelcome, type WelcomePriceState} from "pricing/welcomePrice";
import {
	DEFAULT_EXIT_OFFER_VARIANT, exitOfferAmountsFor, exitOfferPriceFor, shouldShowExitOffer,
	withFirstMonthTerm, type ExitOfferAmounts,
} from "./exitOffer";
import {hasPresentedDiscountThisSession, hasPresentedDiscountToday, recordDiscountPresented, discountAccountKey} from "./offerDismissal";
import {usePaidMoment} from "./usePaidMoment";
import {UpsellApi} from "./UpsellApi";

/** What the pricing dialog needs in order to host the exit offer. */
export interface ExitOfferState {
	/** True while the offer dialog is on screen. */
	open: boolean;
	/**
	 * The amounts the dialog states, in the currency this reader's checkout will charge, or `null` where
	 * their storefront has no intro Price and the offer cannot honestly be phrased at all.
	 *
	 * Resolved HERE rather than in the view (kitecorp/transferify-ui#1846): the dialog used to derive
	 * them itself from the build-baked catalogue, which is a business rule in a component and — once a
	 * geographic storefront existed — a euro quote against a pound charge.
	 */
	amounts: ExitOfferAmounts | null;
	/**
	 * Records a plan CTA press, so a close CAUSED by that press reads as a step towards buying rather
	 * than a departure. Withdrawn again as soon as the pricing sheet is seen still open, because a press
	 * the reader then backed out of is not a purchase.
	 */
	notePurchaseHandoff: () => void;
	/**
	 * Called once the pricing sheet's close transition has finished. Answers what that close WAS, because
	 * this hook is the one place that knows: the paid-moment funnel records a `departure` as the sheet's
	 * dismissal and a `handoff` as nothing at all, and re-deriving the flag beside it would be a second
	 * copy of the clearing rule below.
	 */
	onPricingClosed: () => PricingClose;
	close: () => void;
}

/** How the pricing sheet just closed: on the reader's way to buy, or on their way out. */
export type PricingClose = "handoff" | "departure";

/**
 * The pricing dialog's exit-offer adapter (kitecorp/transferify-ui#1722).
 *
 * A thin React adapter and nothing else, per the repo's presentation/logic split: it holds the open
 * state, reads the account and the storefront, and asks {@link shouldShowExitOffer} — which owns every
 * rule — whether there is a moment here at all.
 *
 * <b>Why the decision is taken on the way OUT rather than on `onClose`.</b> Opening a second modal
 * while the first is still animating away puts two MUI dialogs on screen at once: they fight over the
 * focus trap and over the background's `aria-hidden`, and the reader watches one card slide out from
 * under another. Waiting for the transition also means the pricing sheet's own focus restore has
 * already run, so when the reader eventually closes THIS dialog, MUI's trap returns them to the
 * control that opened pricing in the first place.
 *
 * <b>The session cap is recorded when the offer is SHOWN, not when it is answered.</b> Dismissing it,
 * buying from it and closing the tab are all the same to this record: the interruption has been spent.
 */
export function useExitOffer(
	suppressed: boolean,
	pricingOpen: boolean,
	storefront: StorefrontQuote,
	/**
	 * The pricing page's own welcome answer (kitecorp/transferify#536), an argument for the reason the
	 * storefront is one: the dialog has already read it. It is the account read for a signed-in reader,
	 * which differs from the public run only for an account that has paid before, and the exit offer is
	 * already refused to every such account by `exitOfferEligible`.
	 */
	welcome: WelcomePriceState,
): ExitOfferState {
	const [open, setOpen] = useState(false);
	const [user] = useUser();
	const purchaseHandoff = useRef(false);
	const admissionInFlight = useRef(false);
	const mounted = useRef(true);
	const pricingOpenRef = useRef(pricingOpen);
	pricingOpenRef.current = pricingOpen;
	useEffect(() => {
		mounted.current = true;
		return () => { mounted.current = false; };
	}, []);

	// THE STOREFRONT THE CTA WILL ACTUALLY BE CHARGED FROM, not the one the build baked
	// (kitecorp/transferify-ui#1846).
	//
	// This dialog's CTA goes through `useStartCheckout` -> `PricingApi.getPlan(null)`, which since this
	// ticket accepts whatever storefront the api resolves for the visitor — so a GB reader gets a GBP
	// price id. Phrasing the offer from `bakedPricingForContext` instead would have quoted them
	// "0.99 EUR for 14 days, the full EUR price after" against a pound charge: the wrong glyph on the intro and a
	// renewal figure wrong in both currency and amount.
	//
	// It arrives as an ARGUMENT rather than through a subscription of this hook's own, and that is not a
	// style preference. `PricingDialog` has already resolved this exact storefront for its cards, and it
	// is mounted by `NavBar` on EVERY route — so a second subscription put a second
	// `useState`/`useEffect` pair into the tree of every page in the app to answer a question that was
	// already answered one component up. Taking it as a parameter also keeps this hook honest about the
	// close: `onPricingClosed` runs one commit AFTER the sheet shuts, and the storefront it is handed is
	// whatever the dialog still holds, which is the answer rather than a re-derivation of it.
	const baseAmounts = useMemo(() => exitOfferAmountsFor(storefront), [storefront]);
	// The Plus term's percentage, asked of the api at the moment the offer is about to open and held for
	// this showing (kitecorp/transferify-ui#2994). Null until then and whenever the api quotes none, which
	// is the dialog's two-term shape.
	const [firstMonthPercent, setFirstMonthPercent] = useState<number | null>(null);
	const amounts = useMemo(
		() => baseAmounts === null ? null : withFirstMonthTerm(baseAmounts, firstMonthPercent),
		[baseAmounts, firstMonthPercent],
	);
	// The paid-moment funnel's seam for this surface (kitecorp/transferify-ui#1965). Shown and priced in
	// the same commit, because the offer never opens without its figure, and to EVERY reader it opens for
	// since ui#1983 — the account check that used to sit here made the shown column a floor over signed-in
	// readers on the one surface whose whole audience is anonymous visitors walking away from the pricing
	// table. The beacon routes an anonymous appearance to the anonymous endpoint instead of dropping it,
	// so the checkout that follows a sign-in now has a showing before it. The checkout step is reported by
	// `useExitOfferPurchase`, which is where the redirect actually starts.
	const paidMoment = usePaidMoment("EXIT_OFFER", {
		visible: open,
		price: amounts === null ? null : exitOfferPriceFor(amounts, DEFAULT_EXIT_OFFER_VARIANT),
	});

	// Bumped by every CTA press so the effect below re-runs even when `pricingOpen` did not change.
	const [handoffPresses, setHandoffPresses] = useState(0);

	// THE FLAG IS CLEARED AS SOON AS THE SHEET IS SEEN STILL OPEN, which is what makes it mean what its
	// name says.
	//
	// A CTA press is not evidence of a purchase; a press that CLOSED this sheet is. Today no upgrade
	// verdict closes it at all: `login` and `changePlan` open something above it, `portal` and `account`
	// navigate away and unmount the whole tree, and `checkout` swaps in its own surface. So a flag
	// cleared only on the next open stayed true through the reader dismissing the login modal and
	// pressing the ✕, and swallowed the offer for anonymous visitors, the exact audience this feature
	// exists for.
	//
	// This effect runs on the commit after the press. If the sheet is still open, the press did not hand
	// off and the flag goes. If a verdict DID close the sheet, `pricingOpen` is already false in that
	// same commit, nothing is cleared, and the offer stays suppressed all the way through
	// `onPricingClosed` after the transition. It clears on a fresh open for the same reason.
	useEffect(() => {
		if (pricingOpen) {
			purchaseHandoff.current = false;
		}
	}, [pricingOpen, handoffPresses]);

	const notePurchaseHandoff = useCallback(() => {
		purchaseHandoff.current = true;
		setHandoffPresses(presses => presses + 1);
	}, []);

	const onPricingClosed = useCallback((): PricingClose => {
		const close: PricingClose = purchaseHandoff.current ? "handoff" : "departure";
		const shown = shouldShowExitOffer({
			plan: planOf(user),
			purchaseHandoff: purchaseHandoff.current,
			suppressed,
			seenThisSession: hasPresentedDiscountThisSession() || hasPresentedDiscountToday(discountAccountKey(user)),
			introAmount: amounts?.intro ?? null,
			// Read-only eligibility is the cheap check. The admission request below owns the day allowance.
			eligible: user.exitOfferEligible,
			// Read HERE rather than latched at mount, and from the same stored state the landing picker
			// reads (ui#2020): the code is cleared the instant the api settles the attribution, so a
			// refused signup gets the ordinary offer back on the very next close.
			referred: hasPendingReferral(storedReferralCode(Date.now()), user.referralOffer),
			// Read HERE for the same reason, and from the session's own arrival mark (ui#2111): the
			// pricing sheet a ladder reader closes is usually opened from a different route than the one
			// they landed on, so the fact has to outlive the `/pricing` query it was written from.
			fromLadderLink: arrivedFromLadderLink(),
			welcomeOn: offersYieldToWelcome(welcome),
		});
		if (!shown || admissionInFlight.current) {
			return close;
		}
		admissionInFlight.current = true;
		// The Plus term's quote is asked BESIDE the admission rather than after it, so the dialog opens once
		// with every term it will have: a term appearing a beat later would move the rows under the reader.
		// Both reads are bounded, and a failed quote is only a missing third term.
		void Promise.all([UpsellApi.admitExitOffer(user.isLoggedIn), UpsellApi.getExitOfferFirstMonth()])
			.then(([allowed, percentOff]) => {
				if (allowed && mounted.current && !pricingOpenRef.current
					&& !hasPresentedDiscountThisSession() && !hasPresentedDiscountToday(discountAccountKey(user))) {
					recordDiscountPresented(discountAccountKey(user));
					setFirstMonthPercent(percentOff);
					setOpen(true);
				}
			}).finally(() => { admissionInFlight.current = false; });
		return close;
	}, [user, suppressed, amounts, welcome]);

	// Every route out of the dialog — ✕, scrim, Escape — arrives here, and every one of them is the reader
	// walking away: the CTA's success navigates to Stripe and never closes anything.
	const close = useCallback(() => {
		paidMoment.reportDismissed();
		setOpen(false);
	}, [paidMoment]);

	return {open, amounts, notePurchaseHandoff, onPricingClosed, close};
}
